import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import Input from 'src/shared/components/input/Input';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import FormCard from 'src/shared/components/form-card/FormCard';
import RadioGroup from 'src/shared/components/radio-group/RadioGroup';
import BasicDatePicker from 'src/shared/components/basic-datepicker/BasicDatePicker';
import { toCalendarDate, toDayString } from 'src/shared/components/basic-datepicker/calendarDay';
import type { SchemaViolation } from 'src/shared/state/api-slices/engineErrorMessage';
import AssetEditor from '../asset-editor/AssetEditor';
import { validateItemForm, type FootprintChoice, type ItemFormErrors, type ItemFormValues } from '../../domain/documents/stacItemDocument';
import { DOCUMENT_PATHS, itemRefusals, mergeAssetErrors } from '../../domain/documents/refusals';

type ItemFormProps = {
    mode: 'create' | 'edit';
    initialValues: ItemFormValues;
    isSaving: boolean;
    submitError?: string;
    /** Which rules the engine says the document broke, and where. */
    submitViolations?: SchemaViolation[];
    onSubmit: (values: ItemFormValues) => void;
    /** Reports what the form holds, so the JSON hatch can open on it rather than on what was loaded. */
    onValuesChange?: (values: ItemFormValues) => void;
    onCancel: () => void;
};

const NO_ERRORS: ItemFormErrors = { fields: {}, assets: {} };

/**
 * One version of a dataset, files included.
 *
 * The footprint offers a box or a written geometry: most datasets here are
 * whole-world snapshots, where a box is the whole truth, but a mosaic tile needs a
 * real shape and the engine takes the producer's word for it either way (ADR-016 10a).
 */
const ItemForm = ({
    mode,
    initialValues,
    isSaving,
    submitError,
    submitViolations = [],
    onSubmit,
    onValuesChange,
    onCancel,
}: ItemFormProps) => {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState<ItemFormErrors>(NO_ERRORS);
    /** The rules the engine says this document broke, less any whose field has since been edited. */
    const [refused, setRefused] = useState<SchemaViolation[]>([]);

    // A new refusal replaces the last one; the same one does not re-mark a field since edited.
    const [lastRefusal, setLastRefusal] = useState<string | undefined>();
    if (submitError !== lastRefusal) {
        setLastRefusal(submitError);
        setRefused(submitViolations);
    }

    // Reported from one place rather than from each handler: the footprint radios, the role
    // pips and the asset editor all change `values` without going through the same path, and
    // any of them missed would open the JSON hatch on a stale document.
    useEffect(() => onValuesChange?.(values), [values, onValuesChange]);

    const change = (field: keyof ItemFormValues, value: string) => {
        setValues(previous => ({ ...previous, [field]: value }));
        setErrors(previous => ({ ...previous, fields: { ...previous.fields, [field]: undefined } }));
        setRefused(previous => previous.filter(violation => violation.path.join('/') !== DOCUMENT_PATHS[field]));
    };

    /**
     * A regional footprint cannot be stated here, so the form says so rather than letting a
     * save fail. A version that *already* declares one is exempt: a pipeline can catalogue one
     * through the API, and blocking those would leave them permanently uneditable.
     */
    const isBlockedByFootprint = values.footprint === 'regional' && !values.hasStoredRegionalFootprint;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const found = validateItemForm(values);
        setErrors(found);
        if (Object.keys(found.fields).length === 0 && Object.keys(found.assets).length === 0) {
            onSubmit(values);
        }
    };

    /**
     * A UTC day, held as `YYYY-MM-DD`.
     *
     * The picker works in `Date`s and formats them locally, so the day crosses the boundary as
     * local midnight — a UTC midnight would show as the day before west of Greenwich, and these
     * days decide which version the engine serves.
     */
    const dayField = (name: 'date' | 'inForceUntil', label: string, testId: string) => (
        <BasicDatePicker
            id={testId}
            testId={testId}
            label={label}
            // The same label as every other field in this form wears.
            labelClassName='text-sm font-medium text-primary'
            value={toCalendarDate(values[name])}
            disabled={isSaving}
            hasError={Boolean(shown.fields[name])}
            onChange={chosen => change(name, toDayString(chosen))}
        />
    );

    /**
     * What to show against each input: what this form found wrong, and what the engine did.
     *
     * The engine's refusals are put into the shape validation produces, so every field shows one
     * the way it already shows its own. Validation wins where both speak: it runs on what is on
     * screen now, where a refusal is about the document as it was sent.
     */
    const engineRefusals = itemRefusals(refused, values.assets);
    const shown: ItemFormErrors = {
        fields: { ...engineRefusals.fields, ...errors.fields },
        assets: mergeAssetErrors(engineRefusals.assets, errors.assets),
    };

    const field = (name: keyof ItemFormValues, label: string, testId: string, type = 'text', hint?: string) => (
        <div className='flex flex-col gap-1'>
            <label htmlFor={testId} className='text-sm font-medium text-primary'>
                {label}
            </label>
            <Input
                id={testId}
                data-testid={testId}
                type={type}
                value={values[name] as string}
                disabled={name === 'id' && mode === 'edit'}
                onChange={event => change(name, event.target.value)}
            />
            {hint && !shown.fields[name] && <p className='text-xs text-grey-70'>{hint}</p>}
            {shown.fields[name] && (
                <p className='text-xs text-red-100' data-testid={`${testId}-error`}>
                    {shown.fields[name]}
                </p>
            )}
        </div>
    );

    return (
        <form className='flex w-full max-w-3xl flex-col gap-6' onSubmit={handleSubmit} noValidate>
            <FormCard title='Identity' testId='identity'>
                {field('title', 'Title', 'item-title', 'text', 'Optional. What to call this version — this can be changed later.')}

                {field(
                    'id',
                    'Version identifier',
                    'item-id',
                    'text',
                    mode === 'edit' ? 'A version identifier cannot be renamed.' : 'Unique within this dataset.'
                )}

                {mode === 'create' && (
                    <div
                        className='flex max-w-xl items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2'
                        data-testid='item-id-permanent'
                        role='status'
                    >
                        <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
                        <p className='text-xs text-foreground'>
                            <span className='font-semibold'>The identifier cannot be changed once this version is catalogued.</span> It is
                            the address the engine serves the version at. Use the title above for a name you may want to correct later.
                        </p>
                    </div>
                )}
            </FormCard>

            <FormCard
                title='Computed (UTC)'
                testId='computed'
                description='When the producer computed the data. This is the point-in-time the version becomes effective and an `at` query resolves.'
            >
                <div className='grid grid-cols-2 gap-4'>
                    {dayField('date', 'Date', 'item-date')}
                    {field('time', 'Time', 'item-time', 'time')}
                </div>
                {shown.fields.date && (
                    <p className='text-xs text-red-100' data-testid='item-date-error'>
                        {shown.fields.date}
                    </p>
                )}
            </FormCard>

            <FormCard title='In force until' testId='in-force-until'>
                <p className='text-xs text-muted-foreground' data-testid='item-in-force-until-explainer'>
                    Leave blank while this version is still in force. A version normally stops being current when the next one is computed;
                    set a date here to end it with nothing replacing it — a dataset withdrawn, or a version found to be wrong.
                </p>
                <div className='flex max-w-md items-end gap-2'>
                    <div className='w-48'>{dayField('inForceUntil', 'Closes on', 'item-in-force-until')}</div>
                    {/* Only once there is something to clear: a calendar has no way back to
                        empty, but a control that does nothing still asks to be read.
                        `type='button'` — lifting a closure is a correction to the form, not a
                        save of it. */}
                    {values.inForceUntil !== '' && (
                        <button
                            type='button'
                            data-testid='item-in-force-until-clear'
                            onClick={() => change('inForceUntil', '')}
                            className='rounded px-2 py-1 text-xs font-medium text-primary hover:bg-secondary'
                        >
                            Clear
                        </button>
                    )}
                </div>
                {shown.fields.inForceUntil && (
                    <p className='text-xs text-red-100' data-testid='item-in-force-until-error'>
                        {shown.fields.inForceUntil}
                    </p>
                )}
            </FormCard>

            <FormCard
                title='Footprint'
                testId='footprint'
                description='Where this version’s data applies. Taken as stated — the engine does not check it.'
            >
                <RadioGroup
                    options={[
                        { value: 'global', label: 'Global — the version covers the whole world' },
                        { value: 'regional', label: 'Regional — the version covers part of it' },
                    ]}
                    selectedOption={values.footprint}
                    onChange={chosen => change('footprint', chosen as FootprintChoice)}
                />
                {values.footprint === 'regional' && values.hasStoredRegionalFootprint && (
                    <p className='text-xs text-muted-foreground' data-testid='item-footprint-stored'>
                        This version already declares a narrower footprint. It is kept exactly as catalogued — editing it here is not
                        supported yet.
                    </p>
                )}
            </FormCard>

            <FormCard title='Assets' testId='assets'>
                <p className='text-xs text-muted-foreground'>
                    The objects this version is published as. At least one is required, and every one must be an
                    <code className='mx-1 rounded bg-secondary px-1'>s3://</code>
                    object the engine can read.
                </p>
                {shown.fields.assets && <p className='text-xs text-red-100'>{shown.fields.assets}</p>}
                <AssetEditor
                    assets={values.assets}
                    errors={shown.assets}
                    disabled={isSaving}
                    onChange={assets => {
                        setValues(previous => ({ ...previous, assets }));
                        setErrors(previous => ({ ...previous, assets: {} }));
                        // The engine's refusals about assets go the same way this form's own
                        // do. The editor reports the whole list rather than which field of
                        // which asset changed, so they clear together — leaving one standing
                        // beside a field just edited says it is still wrong when nobody knows.
                        setRefused(previous => previous.filter(violation => violation.path[0] !== 'assets'));
                    }}
                />
            </FormCard>

            {submitError && (
                <p className='text-sm text-red-100' data-testid='item-form-error'>
                    {submitError}
                </p>
            )}

            {/* The callout sits under the buttons, not beside them, so they do not shift
                position when it appears. */}
            <div className='flex flex-col gap-3' data-testid='item-form-actions'>
                <div className='flex gap-3'>
                    <PrimaryButton type='submit' disabled={isSaving || isBlockedByFootprint} data-testid='item-form-submit'>
                        {mode === 'create' ? 'Publish version' : 'Save changes'}
                    </PrimaryButton>
                    <SecondaryButton type='button' onClick={onCancel} disabled={isSaving} data-testid='item-form-cancel'>
                        Cancel
                    </SecondaryButton>
                </div>

                {isBlockedByFootprint && (
                    <div
                        className='flex max-w-xl items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2'
                        data-testid='item-footprint-unsupported'
                        role='status'
                    >
                        <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
                        <p className='text-xs text-foreground'>
                            <span className='font-semibold'>Regional footprints are not supported yet.</span> Drawing one needs a map this
                            form does not have. Set the footprint back to global to save this version.
                        </p>
                    </div>
                )}
            </div>
        </form>
    );
};

export default ItemForm;
